<template>
  <div>
    <Popover class="relative hidden lg:block">
      <PopoverButton
        class="submenu-button group flex items-end hover:cursor-pointer focus:outline-none"
        :class="linkClass"
      >
        <HeartIcon
          class="h-6 w-6 group-hover:fill-current group-focus:fill-current"
        />
        <span class="sr-only">Favorieten</span>
        <span class="text-xs">{{ favorites.length }}</span>
      </PopoverButton>

      <transition
        enter-active-class="transition ease-out duration-200"
        enter-from-class="opacity-0 translate-y-1"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-1"
      >
        <PopoverPanel
          as="ul"
          class="absolute top-full z-10 mt-2 w-72 border border-gray-200 bg-white p-2 shadow-lg"
          :class="isLastItem ? '-right-4' : '-left-2'"
        >
          <li v-if="!favorites.length" class="px-2 py-4 text-sm text-gray-800">
            <p>Je hebt nog geen favoriete vacatures.</p>
            <p class="mt-2">
              Bewaar je favoriete vacatures door op het
              <HeartIcon
                class="inline h-5 w-5"
                aria-label="knop 'Toevoegen aan favorieten'"
              />
              te klikken.
            </p>
          </li>

          <li
            v-for="(favorite, index) in favorites"
            :key="index"
            class="flex flex-col px-3 py-2"
          >
            <span class="text-xs text-gray-800">{{
              favorite.organization
            }}</span>
            <div class="flex items-center justify-between">
              <a
                :href="generateVacancyLink(favorite)"
                class="text-default text-sm hover:underline"
              >
                {{ favorite.title }}
              </a>
              <button
                class="hover:cursor-pointer"
                @click="store.toggleFavorite(favorite)"
              >
                <TrashIcon class="h-4 w-4 text-gray-600 hover:text-red-600" />
                <span class="sr-only">Verwijderen uit favorieten</span>
              </button>
            </div>
          </li>
        </PopoverPanel>
      </transition>
    </Popover>

    <!-- Mobile submenu -->
    <Disclosure
      v-slot="{ open }"
      as="div"
      class="flex w-full flex-col items-center lg:hidden"
    >
      <DisclosureButton
        class="submenu-button flex w-full items-center text-left text-xl font-bold focus:outline-none"
        :class="linkClass"
      >
        <HeartIcon class="h-6 w-6" :class="{ 'fill-current': open }" />
        <span class="mt-auto text-xs">{{ favorites.length }}</span>
        <span class="ml-2">Favorieten</span>

        <component
          :is="open ? MinusIcon : PlusIcon"
          aria-hidden="true"
          class="ml-auto h-7 w-7"
        />
      </DisclosureButton>
      <DisclosurePanel class="mt-2 w-full space-y-2 pl-6" as="ul">
        <li
          v-if="!favorites.length"
          class="px-3 py-2 text-sm"
          :class="
            isBackgroundDark(blok.bgVariant) ? 'text-white' : 'text-gray-800'
          "
        >
          <p>Je hebt nog geen favoriete vacatures.</p>
          <p class="mt-2">
            Bewaar je favoriete vacatures door op het
            <HeartIcon
              class="inline h-5 w-5"
              aria-label="knop 'Toevoegen aan favorieten'"
            />
            te klikken.
          </p>
        </li>

        <li
          v-for="(favorite, index) in favorites"
          :key="index"
          class="flex items-end justify-between px-3 py-2"
          :class="[{ 'border-b': index < favorites.length - 1 }, linkClass]"
        >
          <div class="flex flex-col">
            <span
              class="text-sm"
              :class="
                isBackgroundDark(blok.bgVariant)
                  ? 'text-gray-200'
                  : 'text-gray-800'
              "
              >{{ favorite.organization }}</span
            >
            <a class="text-default" :href="generateVacancyLink(favorite)">
              {{ favorite.title }}
            </a>
          </div>
          <button
            class="mb-1"
            :class="
              isBackgroundDark(blok.bgVariant)
                ? 'text-gray-200'
                : 'text-gray-600'
            "
            @click="store.toggleFavorite(favorite)"
          >
            <TrashIcon class="h-4 w-4" />
            <span class="sr-only">Verwijderen uit favorieten</span>
          </button>
        </li>
      </DisclosurePanel>
    </Disclosure>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/vue'
import { HeartIcon, TrashIcon } from '@heroicons/vue/24/outline'
import { PlusIcon, MinusIcon } from '@heroicons/vue/24/solid'
import { useColorPalette } from '../composables/colorPalette'
import { useMainStore } from '../store'
import { storeToRefs } from 'pinia'
import hydrateObject from '../utils/hydrateObject'

const props = defineProps({
  blok: {
    type: Object,
    default: () => {},
  },
  context: {
    type: Object,
    default: () => {},
  },
  item: {
    type: Object,
    default: () => {},
  },
  isLastItem: {
    type: Boolean,
    default: false,
  },
})

const store = useMainStore()
const { favorites } = storeToRefs(store)

const { textColorClass, isBackgroundDark } = useColorPalette()

const linkClass = computed(() => {
  if (props.blok.linkColor) {
    return textColorClass(props.blok.linkColor)
  }
  return isBackgroundDark(props.blok.bgVariant)
    ? 'text-gray-100 hover:text-white focus:text-white'
    : 'text-gray-800 hover:text-gray-900 focus:text-gray-900'
})

function generateVacancyLink(favorite) {
  // Convert something like: https://www.gemeentebanen.nl/vacatures/{{vacancy.id}}/{{vacancy.company.slug}}/{{vacancy.slug}}
  // to the real URL
  if (props.context.site.vacancyLinkFormat) {
    const context = {
      vacancy: {
        id: favorite.id,
        slug: favorite.vacancySlug,
        company: {
          slug: favorite.companySlug,
        },
      },
    }
    const { vacancyUrl } = hydrateObject(
      { vacancyUrl: props.context.site.vacancyLinkFormat },
      context,
    )

    const linkUrl = new URL(vacancyUrl)
    if (import.meta.env.VITE_IS_EDITOR) {
      return `/${props.context.domainSlug}${linkUrl.pathname}`
    } else {
      return linkUrl.pathname
    }
  } else {
    return `/${favorite.id}`
  }
}
</script>

<style>
.submenu-button:hover,
.submenu-button:focus {
  text-shadow: 0 0 0.8px;
}

.menu-subitem > div {
  width: 100%;
}
</style>
